import React from 'react';
import { Link } from 'react-router-dom';
import { Microscope, Users, Leaf, ShieldCheck, Dna, Activity, FileCheck, Building2, Globe2 } from 'lucide-react';
export function SciencePage() {
  return <div className="bg-white min-h-screen pb-20" data-id="element-581">
      {/* Page Header */}
      <div className="bg-gray-50 border-b border-gray-200 py-16 px-4 sm:px-6 lg:px-8" data-id="element-582">
        <div className="max-w-4xl mx-auto text-center" data-id="element-583">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-id="element-584">
            Science Behind GNLD NeoLife Products
          </h1>
          <div className="w-24 h-1 bg-[#0e7be1] mx-auto rounded-full" data-id="element-585"></div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16" data-id="element-586">
        {/* Section 1 */}
        <section data-id="element-587">
          <div className="flex items-center gap-3 mb-4" data-id="element-588">
            <Microscope className="w-6 h-6 text-[#0e7be1]" data-id="element-589" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-590">
              GNLD NeoLife Scientific Advisory Board
            </h2>
          </div>
          <div className="prose prose-lg text-gray-600 max-w-none" data-id="element-591">
            <p className="mb-4" data-id="element-592">
              GNLD NeoLife’s Scientific Advisory Board (SAB) sits at the core of
              a worldwide network of leading scientists, doctors, and research
              institutions dedicated to developing the finest, safest, and most
              effective nutritional products available.
            </p>
            <p data-id="element-593">
              This highly respected global team guides product research,
              formulation, and validation to ensure every NeoLife product is
              rooted in nature and supported by science. Their collective
              expertise provides a depth of knowledge, insight, and opportunity
              that few companies in the nutrition industry can match.
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section data-id="element-594">
          <div className="flex items-center gap-3 mb-4" data-id="element-595">
            <Users className="w-6 h-6 text-[#0e7be1]" data-id="element-596" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-597">
              Created by Doctors and Scientists
            </h2>
          </div>
          <div className="prose prose-lg text-gray-600 max-w-none" data-id="element-598">
            <p data-id="element-599">
              NeoLife’s Scientific Advisory Board is composed of world-class
              doctors, scientists, and health experts who share a commitment to
              advancing human health through nutrition. Every formula is
              carefully designed using whole-food, human-food-chain ingredients
              and developed with long-term safety and effectiveness in mind.
            </p>
          </div>
        </section>

        {/* Section 3 */}
        <section data-id="element-600">
          <div className="flex items-center gap-3 mb-4" data-id="element-601">
            <Dna className="w-6 h-6 text-[#0e7be1]" data-id="element-602" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-603">
              Whole Food Based Cellular Nutrition
            </h2>
          </div>
          <div className="prose prose-lg text-gray-600 max-w-none" data-id="element-604">
            <p className="mb-4" data-id="element-605">
              For over six decades, families around the world have relied on
              NeoLife to deliver nutrition that supports cellular health.
              NeoLife products are based on whole foods found naturally in the
              human food chain and selected for their bio-efficiency.
            </p>
            <p data-id="element-606">
              Because health begins at the cellular level, NeoLife uses nutrient
              forms that are optimized for absorption, utilization, and benefit
              within the body.
            </p>
          </div>
        </section>

        {/* Section 4 */}
        <section data-id="element-607">
          <div className="flex items-center gap-3 mb-4" data-id="element-608">
            <Leaf className="w-6 h-6 text-[#0e7be1]" data-id="element-609" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-610">
              Defining Phytonutrients
            </h2>
          </div>
          <div className="prose prose-lg text-gray-600 max-w-none" data-id="element-611">
            <p className="mb-4" data-id="element-612">
              Phytonutrients are powerful natural compounds found in plants that
              help protect them from environmental stress. These compounds
              include plant pigments responsible for the rich colors of fruits
              and vegetables.
            </p>
            <p data-id="element-613">
              When consumed as part of whole foods, phytonutrients provide
              antioxidant support and contribute to long-term health and
              vitality.
            </p>
          </div>
        </section>

        {/* Section 5 - Chain of Life */}
        <section className="bg-blue-50 p-8 rounded-2xl border border-blue-100" data-id="element-614">
          <div className="flex items-center gap-3 mb-6" data-id="element-615">
            <Activity className="w-6 h-6 text-[#0e7be1]" data-id="element-616" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-617">
              The Chain of Life
            </h2>
          </div>
          <div className="prose prose-lg text-gray-600 max-w-none mb-6" data-id="element-618">
            <p data-id="element-619">
              NeoLife defines optimal nutrition through six interconnected
              groups of nutrients known as the “Chain of Life.” Each link
              supports and depends on the others:
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-620">
            {[{
            title: 'Lipids and sterols',
            desc: 'Essential fats that support cellular structure and energy'
          }, {
            title: 'Carbohydrates',
            desc: 'The body’s primary fuel source for energy and endurance'
          }, {
            title: 'Proteins',
            desc: 'Required for building and repairing cells, muscles, skin, and organs'
          }, {
            title: 'Enzymes',
            desc: 'Biochemical catalysts essential for digestion and metabolism'
          }, {
            title: 'Minerals',
            desc: 'Support bones, teeth, soft tissue, and muscle function'
          }, {
            title: 'Vitamins and food-related factors',
            desc: 'Including phytonutrients such as carotenoids and flavonoids that promote lifelong health'
          }].map((item, idx) => <div key={idx} className="bg-white p-4 rounded-lg shadow-sm border border-blue-100" data-id="element-621">
                <h3 className="font-bold text-[#0e7be1] mb-1" data-id="element-622">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-snug" data-id="element-623">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </section>

        {/* Section 6 */}
        <section data-id="element-624">
          <div className="flex items-center gap-3 mb-4" data-id="element-625">
            <ShieldCheck className="w-6 h-6 text-[#0e7be1]" data-id="element-626" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-627">
              Non-GMO and Full Traceability
            </h2>
          </div>
          <div className="prose prose-lg text-gray-600 max-w-none" data-id="element-628">
            <p className="mb-4" data-id="element-629">
              NeoLife products are made exclusively with non-GMO ingredients and
              are routinely tested for purity and potential contaminants.
              Suppliers are required to certify that all raw materials are free
              from genetically modified organisms.
            </p>
            <p data-id="element-630">
              NeoLife maintains full traceability from farm to table through a
              comprehensive quality assurance process that includes seed
              selection, crop monitoring, nutrient optimization, laboratory
              analysis, and batch tracking.
            </p>
          </div>
        </section>

        {/* Section 7 */}
        <section data-id="element-631">
          <div className="flex items-center gap-3 mb-4" data-id="element-632">
            <FileCheck className="w-6 h-6 text-[#0e7be1]" data-id="element-633" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-634">
              Clinically Proven Human Clinical Studies
            </h2>
          </div>
          <div className="prose prose-lg text-gray-600 max-w-none" data-id="element-635">
            <p className="mb-4" data-id="element-636">
              NeoLife products are supported by human clinical studies published
              in respected scientific journals, including the Journal of the
              American College of Nutrition, the International Journal of
              Toxicology, the FASEB Journal, and the American Journal of
              Clinical Nutrition.
            </p>
            <p data-id="element-637">
              Unlike many supplement companies that rely on animal or yeast
              studies, NeoLife prioritizes human clinical research to validate
              product effectiveness and safety.
            </p>
          </div>
        </section>

        {/* Section 8 */}
        <section data-id="element-638">
          <div className="flex items-center gap-3 mb-4" data-id="element-639">
            <Building2 className="w-6 h-6 text-[#0e7be1]" data-id="element-640" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-641">
              Pharmaceutical License for Manufacturing
            </h2>
          </div>
          <div className="prose prose-lg text-gray-600 max-w-none" data-id="element-642">
            <p className="mb-4" data-id="element-643">
              NeoLife owns and operates state-of-the-art manufacturing
              facilities that exceed global Good Manufacturing Practice (GMP)
              standards.
            </p>
            <p data-id="element-644">
              The company holds pharmaceutical manufacturing licenses from the
              State of California and the Therapeutic Goods Administration (TGA)
              of Australia, along with FDA registration and regulatory
              compliance.
            </p>
          </div>
        </section>

        {/* Section 9 */}
        <section data-id="element-645">
          <div className="flex items-center gap-3 mb-4" data-id="element-646">
            <Globe2 className="w-6 h-6 text-[#0e7be1]" data-id="element-647" />
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-648">
              Strategic Partnerships and Global Research
            </h2>
          </div>
          <div className="prose prose-lg text-gray-600 max-w-none" data-id="element-649">
            <p className="mb-4" data-id="element-650">
              NeoLife’s scientific credibility has led to partnerships with
              respected organizations such as the United States Department of
              Agriculture, UNESCO, Stanford University Health Library, and major
              research institutions worldwide.
            </p>
            <p data-id="element-651">
              Through ongoing collaboration, research funding, and educational
              initiatives, NeoLife continues to advance the role of nutrition in
              disease prevention and long-term health.
            </p>
          </div>
        </section>

        {/* Closing */}
        <div className="mt-16 pt-8 border-t border-gray-200 text-center" data-id="element-652">
          <p className="text-lg text-gray-700" data-id="element-653">
            For an overview of NeoLife’s philosophy and mission, see the video
            on our{' '}
            <Link to="/" className="text-[#0e7be1] font-semibold hover:underline" data-id="element-654">
              Homepage
            </Link>
            .
          </p>
        </div>
      </div>
    </div>;
}